import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { useMe } from '../me/use-me.js';
import { ProviderForm } from '../integrations/providers/ProviderForm.js';
import { useConnections } from '../integrations/use-integrations.js';
import { MODULE_FIELDS } from './module-defs.js';
import {
  useDisableModule,
  useEnableModule,
  useModule,
  useSaveModule,
  type IntegrationRoleRequirement,
  type ModuleId,
  type RoleBindingStatus,
} from './use-modules.js';

const VALID_MODULES: ReadonlyArray<ModuleId> = ['evl', 'offer', 'termination'];

function isModule(s: string | undefined): s is ModuleId {
  return !!s && (VALID_MODULES as readonly string[]).includes(s);
}

export function ModuleDetailPage(): JSX.Element {
  const { companyId, moduleId } = useParams<{ companyId: string; moduleId: string }>();
  if (!companyId || !isModule(moduleId)) {
    return <p className="text-slate-500">Unknown module.</p>;
  }
  return <Inner companyId={companyId} moduleId={moduleId} />;
}

function Inner({ companyId, moduleId }: { companyId: string; moduleId: ModuleId }): JSX.Element {
  const { me } = useMe();
  const { data, isLoading, error } = useModule(companyId, moduleId);
  const connections = useConnections(companyId);
  const save = useSaveModule(companyId, moduleId);
  const enable = useEnableModule(companyId, moduleId);
  const disable = useDisableModule(companyId, moduleId);
  const defs = MODULE_FIELDS[moduleId];

  // Track the user's in-progress binding edits locally so we can submit
  // both the config + bindings from the same form.
  const [bindingDraft, setBindingDraft] = useState<Record<string, string>>({});

  useEffect(() => {
    if (data?.integrationBindings) {
      setBindingDraft({ ...data.integrationBindings });
    }
  }, [data?.integrationBindings]);

  const role = useMemo(() => {
    const ionic = (me?.ionicRoles ?? []).length > 0;
    if (ionic) return 'admin' as const;
    const company = (me?.companies ?? []).find((c) => c.companyId === companyId);
    if (!company) return 'none' as const;
    if (company.roles.includes('company_admin')) return 'admin' as const;
    if (company.roles.includes('company_hr')) return 'hr' as const;
    return 'viewer' as const;
  }, [me, companyId]);

  const editable = role === 'admin';

  if (role === 'none' || role === 'viewer') {
    return (
      <div className="rounded-md border border-slate-200 bg-white p-4 text-slate-600">
        You don&rsquo;t have permission to view this module.
      </div>
    );
  }

  if (isLoading) return <p className="text-slate-500">Loading…</p>;
  if (error || !data) {
    return (
      <div className="rounded-md border border-red-300 bg-red-50 p-4 text-red-800">
        <p className="font-medium">Could not load module.</p>
        <p className="text-sm">{error?.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/companies/${companyId}/modules`}
          className="text-sm text-brand-600 hover:text-brand-700"
        >
          ← Back to modules
        </Link>
        <h1 className="mt-1 text-2xl font-semibold">{data.title}</h1>
        <p className="mt-1 max-w-2xl text-sm text-slate-600">{data.description}</p>
        <p className="mt-1 text-xs text-slate-500">
          Current schema: v{data.registryVersion}
          {data.storedVersion !== null
            ? data.storedVersion === data.registryVersion
              ? ' (up to date)'
              : ` (stored v${data.storedVersion} — upgrade available)`
            : ' (not yet configured)'}
        </p>
        {moduleId === 'evl' && data.enabled && (
          <Link
            to={`/companies/${companyId}/modules/evl/runs/new`}
            className="mt-3 inline-flex rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-brand-700"
          >
            + Start a new EVL run
          </Link>
        )}
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <h2 className="mb-3 text-base font-semibold text-slate-800">Integration role bindings</h2>
        <p className="mb-4 text-sm text-slate-600">
          Each role is bound to one named connection for its provider. Unbound roles fall back to{' '}
          <span className="font-mono">default</span> automatically on save.
        </p>
        <div className="space-y-3">
          {data.requiresIntegrations.map((req) => (
            <RoleBindingRow
              key={req.role}
              companyId={companyId}
              requirement={req}
              status={data.roleBindings.find((b) => b.role === req.role)}
              connections={connections.data?.items.filter((c) => c.provider === req.provider) ?? []}
              value={bindingDraft[req.role] ?? 'default'}
              disabled={!editable}
              onChange={(v) => setBindingDraft((prev) => ({ ...prev, [req.role]: v }))}
            />
          ))}
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <h2 className="mb-3 text-base font-semibold text-slate-800">Configuration</h2>
        <ProviderForm
          fields={defs.fields}
          initialValues={(data.config as Record<string, unknown>) ?? {}}
          hasStoredSecret={false}
          editable={editable}
          submitLabel={data.storedVersion === null ? 'Save' : 'Save changes'}
          pending={save.isPending}
          onSubmit={async (values) => {
            const normalised = normaliseConfig(moduleId, values);
            await save.mutateAsync({
              config: normalised,
              integrationBindings: bindingDraft,
            });
          }}
        />
        {save.error && (
          <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">
            Save failed: {save.error.message}
          </p>
        )}
      </section>

      {editable && data.storedVersion !== null && (
        <section className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-4">
          {data.enabled ? (
            <button
              type="button"
              onClick={() => disable.mutate()}
              disabled={disable.isPending}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
            >
              {disable.isPending ? 'Disabling…' : 'Disable module'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => enable.mutate()}
              disabled={enable.isPending || !data.canEnable}
              title={
                !data.canEnable
                  ? 'Bind every required role to a connected connection first'
                  : undefined
              }
              className={clsx(
                'rounded-md px-3 py-1.5 text-sm font-medium shadow-sm',
                data.canEnable
                  ? 'bg-brand-600 text-white hover:bg-brand-700'
                  : 'bg-slate-200 text-slate-500',
              )}
            >
              {enable.isPending ? 'Enabling…' : 'Enable module'}
            </button>
          )}
          {data.unmetRoles.length > 0 && (
            <p className="text-xs text-amber-800">
              Unmet roles: <span className="font-mono">{data.unmetRoles.join(', ')}</span>
            </p>
          )}
          {enable.error && (
            <p className="text-sm text-red-700">Enable failed: {enable.error.message}</p>
          )}
          {disable.error && (
            <p className="text-sm text-red-700">Disable failed: {disable.error.message}</p>
          )}
        </section>
      )}
    </div>
  );
}

function RoleBindingRow({
  companyId,
  requirement,
  status,
  connections,
  value,
  disabled,
  onChange,
}: {
  companyId: string;
  requirement: IntegrationRoleRequirement;
  status: RoleBindingStatus | undefined;
  connections: Array<{ name: string; status: string }>;
  value: string;
  disabled: boolean;
  onChange: (v: string) => void;
}): JSX.Element {
  const hasSelected = connections.some((c) => c.name === value);
  return (
    <div className="rounded-md border border-slate-200 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-800">
            <span className="font-mono">{requirement.role}</span> · {requirement.provider}
            {requirement.optional && (
              <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs font-normal text-slate-600">
                optional
              </span>
            )}
          </p>
          <p className="mt-0.5 text-xs text-slate-500">{requirement.description}</p>
        </div>
        <div className="shrink-0">
          {status && <StatusChip status={status.connectionStatus} optional={status.optional} />}
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <label className="text-xs text-slate-600">Connection:</label>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm font-mono"
        >
          {!hasSelected && <option value={value}>{value} (not configured yet)</option>}
          {connections.map((c) => (
            <option key={c.name} value={c.name}>
              {c.name} ({c.status})
            </option>
          ))}
        </select>
        <Link
          to={`/companies/${companyId}/integrations/${requirement.provider}/${value}`}
          className="text-xs text-brand-600 hover:text-brand-700"
        >
          Manage →
        </Link>
      </div>
    </div>
  );
}

function StatusChip({
  status,
  optional,
}: {
  status: 'unconfigured' | 'connected' | 'failing' | null;
  optional: boolean;
}): JSX.Element {
  if (status === 'connected') {
    return (
      <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
        connected ✓
      </span>
    );
  }
  if (status === null) {
    return (
      <span
        className={clsx(
          'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
          optional ? 'bg-slate-100 text-slate-600' : 'bg-red-100 text-red-800',
        )}
      >
        {optional ? 'optional · unset' : 'missing'}
      </span>
    );
  }
  return (
    <span
      className={clsx(
        'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
        status === 'failing' ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-700',
      )}
    >
      {status}
    </span>
  );
}

function normaliseConfig(
  moduleId: ModuleId,
  raw: Record<string, unknown>,
): Record<string, unknown> {
  if (moduleId === 'evl') {
    return {
      ...raw,
      highFrequencyThreshold: Number(raw.highFrequencyThreshold ?? 3),
      highFrequencyWindowDays: Number(raw.highFrequencyWindowDays ?? 30),
    };
  }
  if (moduleId === 'termination') {
    const cadence =
      typeof raw.reminderCadenceDays === 'string'
        ? raw.reminderCadenceDays
            .split(',')
            .map((s) => Number(s.trim()))
            .filter((n) => Number.isFinite(n))
        : raw.reminderCadenceDays;
    return {
      ...raw,
      reminderCadenceDays: cadence ?? [3, 7],
      countryChecklistFlags: raw.countryChecklistFlags ?? {
        cobraForUs: true,
        vacationPayoutForCa: true,
        vacationPayoutForUs: false,
      },
    };
  }
  return raw;
}
